import Policy from "./abstracts/Policy";
import { ForumCommentModel, ForumThreadModel } from "@/models";
import { auth } from "@/services";

export interface iForumCommentPolicy {
  /**
   * Checks whether the user can create a new forum comment.
   *
   * @param {ForumThreadModel=} forumThread The thread this comment will belong to.
   * @param {ForumCommentModel=} forumComment The parent comment.
   * @returns {boolean}
   */
  canCreate(
    forumThread?: ForumThreadModel,
    forumComment?: ForumCommentModel,
  ): boolean;

  /**
   * Checks whether the user can delete a forum comment.
   *
   * @param {ForumCommentModel=} forumComment The comment.
   * @param {ForumThreadModel=} forumThread The thread this comment belongs to.
   * @returns {boolean}
   */
  canDelete(
    forumComment?: ForumCommentModel,
    forumThread?: ForumThreadModel,
  ): boolean;

  /**
   * Checks whether the user can edit a forum comment.
   *
   * @param {ForumCommentModel=} forumComment The comment.
   * @param {ForumThreadModel=} forumThread The thread this comment belongs to.
   */
  canEdit(
    forumComment?: ForumCommentModel,
    forumThread?: ForumThreadModel,
  ): boolean;
}

export class ForumComment extends Policy implements iForumCommentPolicy {
  /**
   * Checks whether the user can create a new forum comment.
   *
   * @param {ForumThreadModel=} forumThread The thread this comment will belong to.
   * @param {ForumCommentModel=} forumComment The parent comment.
   * @returns {boolean}
   */
  public canCreate(
    forumThread?: ForumThreadModel,
    forumComment?: ForumCommentModel,
  ): boolean {
    return !!this.authService.user();
  }

  /**
   * Checks whether the user can delete a forum comment.
   *
   * @param {ForumCommentModel=} forumComment The comment
   * @param {ForumThreadModel=} forumThread The thread this comment belongs to
   * @returns {boolean}
   */
  public canDelete(
    forumComment?: ForumCommentModel,
    forumThread?: ForumThreadModel,
  ): boolean {
    const user = this.authService.user();

    if (!user) {
      return false;
    }

    // users can delete their own comments
    if (forumComment && forumComment.data.user_id === user.data.id) {
      return true;
    }

    return this.authService.hasPermission("forum-threads.manage");
  }

  /**
   * Checks whether the user can edit a forum comment.
   *
   * @param {ForumCommentModel=} forumComment The comment.
   * @param {ForumThreadModel=} forumThread The thread this comment belongs to.
   */
  canEdit(
    forumComment?: ForumCommentModel,
    forumThread?: ForumThreadModel,
  ): boolean {
    const user = this.authService.user();

    if (!user) {
      return false;
    }

    // users can edit their own comments
    if (forumComment && forumComment.data.user_id === user.data.id) {
      return true;
    }

    return this.authService.hasPermission("forum-threads.manage");
  }
}

export default new ForumComment(auth);
